import { lazy, Suspense, useEffect, useRef } from "react";

/// Components
import Index from "./jsx";
import { connect, useDispatch } from "react-redux";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
// action
// import { checkAutoLogin } from './services/AuthService';
import { isAuthenticated } from "./store/selectors/AuthSelectors";
/// Style

import "./assets/css/style.css";
import "./assets/css/start-now-wizard.css";
import ActivateAccount from "./jsx/pages/ActivateAccount";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ResetPassword from "./jsx/pages/ResetPassword";
import AcceptFranchiseInvite from "./jsx/pages/AcceptFranchiseInvite";
import AcceptRestaurantJoin from "./jsx/pages/AcceptRestaurantJoin";
import OrderNotifier from "./OrderNotif";
import { fetchRestoDataByUser } from "./services/restaurantService";
import { setRestaurantCurrencyCode, setRestaurantSecondaryCurrencyCode } from "./utils/currency";
import { LOGOUT_ACTION } from "./store/actions/AuthActions";
import { isFranchisePortalSession } from "./utils/franchiseSession";
import {
  endTabScopedAuthSession,
  getAuthToken,
  isRestaurantImpersonationAuthSession,
} from "./utils/authToken";
import { safeNextPath } from "./utils/safeNextPath";
import {
  clearRestaurantSession,
  forceLogoutToLogin,
  isTokenExpired,
} from "./utils/sessionAuth";

const PASSWORD_RESET_LOGOUT_EVENT = "restaurantPasswordResetLogout";

const SignUp = lazy(() => import("./jsx/pages/Registration"));
const ForgotPassword = lazy(() => import("./jsx/pages/ForgotPassword"));
const Login = lazy(() => {
  return new Promise((resolve) => {
    setTimeout(() => resolve(import("./jsx/pages/Login")), 500);
  });
});

function withRouter(Component) {
  function ComponentWithRouterProp(props) {
    let location = useLocation();
    let navigate = useNavigate();
    let params = useParams();

    return <Component {...props} router={{ location, navigate, params }} />;
  }

  return ComponentWithRouterProp;
}

function RedirectToLogin() {
  const location = useLocation();
  const next = safeNextPath(`${location.pathname}${location.search || ""}`);
  return (
    <Navigate
      to={next ? `/login?next=${encodeURIComponent(next)}` : "/login"}
      replace
    />
  );
}

function App(props) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const token = getAuthToken();
  const location = useLocation();
  const archiveLogoutTriggered = useRef(false);

  const forceSessionLogout = () => {
    if (archiveLogoutTriggered.current) return;
    archiveLogoutTriggered.current = true;
    clearRestaurantSession();
    dispatch({ type: LOGOUT_ACTION });
    forceLogoutToLogin();
  };

  useEffect(() => {
    const handlePasswordResetLogout = (event) => {
      if (event.key !== PASSWORD_RESET_LOGOUT_EVENT) return;

      endTabScopedAuthSession();
      [
        "tokenLogin",
        "email",
        "userDetails",
        "restaurantId",
        "restaurantCurrency",
        "restaurantSecondaryCurrency",
        "userRole",
        "franchiseFK",
        "isOwner",
        "userPermissions",
      ].forEach((key) => localStorage.removeItem(key));
      dispatch({ type: LOGOUT_ACTION });

      if (window.location.pathname !== "/login") {
        navigate("/login", { replace: true });
      }
    };

    window.addEventListener("storage", handlePasswordResetLogout);
    return () =>
      window.removeEventListener("storage", handlePasswordResetLogout);
  }, [dispatch, navigate]);

  useEffect(() => {
    const isAuthPage =
      location.pathname === "/login" ||
      location.pathname === "/page-register" ||
      location.pathname === "/page-forgot-password" ||
      location.pathname.startsWith("/reset-password") ||
      location.pathname.startsWith("/accept-franchise-invite") ||
      location.pathname.startsWith("/accept-restaurant-join") ||
      /^\/activate-account\//.test(location.pathname);

    const currentToken = getAuthToken();
    if (currentToken && isTokenExpired(currentToken)) {
      clearRestaurantSession();
      dispatch({ type: LOGOUT_ACTION });
      if (!isAuthPage) {
        navigate("/login", { replace: true });
      }
      return;
    }

    if (!currentToken && !isAuthPage) {
      dispatch({ type: LOGOUT_ACTION });
      navigate("/login", { replace: true });
    }
  }, [dispatch, location.pathname, navigate]);

  useEffect(() => {
    if (
      location.pathname.startsWith("/reset-password") ||
      location.pathname.startsWith("/accept-franchise-invite") ||
      location.pathname.startsWith("/accept-restaurant-join") ||
      location.pathname === "/login" ||
      location.pathname === "/page-forgot-password" ||
      location.pathname === "/page-register" ||
      /^\/activate-account\//.test(location.pathname)
    ) {
      return;
    }

    const fetchRestaurantData = async () => {
      if (isFranchisePortalSession()) {
        return;
      }
      try {
        const result = await fetchRestoDataByUser();
        if (!result?.restaurant) return;

        if (
          result.restaurant.archive &&
          !isRestaurantImpersonationAuthSession()
        ) {
          forceSessionLogout();
          return;
        }

        archiveLogoutTriggered.current = false;
        // Initialize app currency from persisted restaurant settings.
        setRestaurantCurrencyCode(result.restaurant.currency);
        setRestaurantSecondaryCurrencyCode(result.restaurant.secondaryCurrency);
      } catch (error) {
        const code = error?.response?.data?.code;
        if (
          (code === "RESTAURANT_ARCHIVED" &&
            !isRestaurantImpersonationAuthSession()) ||
          code === "TOKEN_REVOKED" ||
          code === "ACCOUNT_DEACTIVATED"
        ) {
          forceSessionLogout();
          return;
        }
        console.error("Error loading restaurant data:", error);
      }
    };
    if (token && !isTokenExpired(token)) {
      fetchRestaurantData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, location.pathname, dispatch]);

  const isAuthPage =
    location.pathname === "/login" ||
    location.pathname === "/page-register" ||
    location.pathname === "/page-forgot-password" ||
    location.pathname.startsWith("/reset-password") ||
    location.pathname.startsWith("/accept-franchise-invite") ||
    location.pathname.startsWith("/accept-restaurant-join") ||
    /^\/activate-account\//.test(location.pathname);

  const isLoggedIn = Boolean(
    props.isAuthenticated && token && !isTokenExpired(token)
  );

  let routeblog = (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/activate-account/:email" element={<ActivateAccount />} />
      <Route path="/page-register" element={<SignUp />} />
      <Route path="/page-forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/reset-password/:token" element={<ResetPassword />} />
      <Route path="/accept-franchise-invite" element={<AcceptFranchiseInvite />} />
      <Route path="/accept-franchise-invite/:token" element={<AcceptFranchiseInvite />} />
      <Route path="/accept-restaurant-join" element={<AcceptRestaurantJoin />} />
      <Route path="/accept-restaurant-join/:token" element={<AcceptRestaurantJoin />} />
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="*" element={<RedirectToLogin />} />
    </Routes>
  );

  if (
    location.pathname.startsWith("/reset-password") ||
    location.pathname.startsWith("/accept-franchise-invite") ||
    location.pathname.startsWith("/accept-restaurant-join")
  ) {
    return (
      <div className="vh-100">
        <Suspense
          fallback={
            <div id="preloader">
              <div className="sk-three-bounce">
                <div className="sk-child sk-bounce1"></div>
                <div className="sk-child sk-bounce2"></div>
                <div className="sk-child sk-bounce3"></div>
              </div>
            </div>
          }
        >
          {routeblog}
        </Suspense>
        <ToastContainer />
      </div>
    );
  }

  // Logged in but on auth page -> redirect
  if (isLoggedIn && isAuthPage) {
    const next = safeNextPath(new URLSearchParams(location.search).get("next"));
    return (
      <Navigate
        to={
          next ||
          (isFranchisePortalSession() ? "/franchise-restaurants" : "/dashboard")
        }
        replace
      />
    );
  }

  // eslint-disable-next-line react/prop-types
  if (isLoggedIn) {
    return (
      <>
        <Suspense
          fallback={
            <div id="preloader">
              <div className="sk-three-bounce">
                <div className="sk-child sk-bounce1"></div>
                <div className="sk-child sk-bounce2"></div>
                <div className="sk-child sk-bounce3"></div>
              </div>
            </div>
          }
        >
          <Index />
          <OrderNotifier />
        </Suspense>
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />
      </>
    );
  }

  return (
    <div className="vh-100">
      <Suspense
        fallback={
          <div id="preloader">
            <div className="sk-three-bounce">
              <div className="sk-child sk-bounce1"></div>
              <div className="sk-child sk-bounce2"></div>
              <div className="sk-child sk-bounce3"></div>
            </div>
          </div>
        }
      >
        {routeblog}
      </Suspense>
      <ToastContainer />
    </div>
  );
}

const mapStateToProps = (state) => {
  return {
    isAuthenticated: isAuthenticated(state),
  };
};

//export default connect((mapStateToProps)(App));
export default withRouter(connect(mapStateToProps)(App));
